import { useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { format } from 'date-fns';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import { ProgressBar, StatusChip } from '../quotas/QuotaTable';
import ConditionBuilder from './ConditionBuilder';
import { indexQuestions } from './conditions';
import { fieldErrors, wranglerError } from './errors';
import { IncreaseDialog, SetLimitDialog } from './LimitDialogs';
import LogicSummary from './LogicSummary';
import { fromLogic, sameLogic, toLogic, validate } from './logicModel';
import {
  BuilderArea, FooterActions, Panel, PanelAlerts, PanelHeader, PanelSection, SummaryBox,
} from './PanelLayout';
import { countingSkips, skipCounts, uncountedSkips } from './skips';

const signed = (n) => (n >= 0 ? `+${n}` : `−${Math.abs(n)}`);

const quoted = (index, id) => `“${safeDecode(index.get(id)?.title || `Question ${id}`)}”`;
const andList = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** "4, or 5 counting people who skip" (or just "4" when Wrangler gave no count with skips). */
const withSkips = (count, skips) => (skips == null ? `${count}` : `${count}, or ${skips} counting people who skip`);

/**
 * The import review's skip question (Wrangler v1.4.0): Alchemer's "is not one of" came in as
 * "answered, and not one of", which leaves out people who skipped. Yes changes each such condition
 * to (not_in OR not_answered); No keeps it.
 */
const SkipQuestion = ({ questionIds, index, countCheck, showCounts, blockedBy, busy, onYes, onNo }) => {
  const several = questionIds.length > 1;
  const named = (ids) => andList(ids.map((id) => quoted(index, id)));
  // Wrangler lists questions that aren't required (false) or whose setting Alchemer doesn't report (null).
  const notRequired = questionIds.filter((id) => index.get(id)?.required === false);
  const unknown = questionIds.filter((id) => index.get(id)?.required !== false);
  const isAre = (ids) => (ids.length > 1 ? 'are' : 'is');
  const counts = showCounts ? skipCounts(countCheck) : null;
  return (
    <Alert severity="warning">
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        Count people who skip {several ? 'these questions' : 'this question'}?
      </Typography>
      <Box>
        {unknown.length === 0 && (
          <>{named(notRequired)} {isAre(notRequired)}n&apos;t required in Alchemer, so some respondents leave {several ? 'them' : 'it'} blank.</>
        )}
        {notRequired.length === 0 && (
          <>Alchemer doesn&apos;t say whether {named(unknown)} {isAre(unknown)} required, so some respondents may leave {several ? 'them' : 'it'} blank.</>
        )}
        {notRequired.length > 0 && unknown.length > 0 && (
          <>
            {named(notRequired)} {isAre(notRequired)}n&apos;t required in Alchemer, and Alchemer doesn&apos;t say whether{' '}
            {named(unknown)} {isAre(unknown)}, so some respondents may leave them blank.
          </>
        )}
        {' '}This quota&apos;s &ldquo;answered, and not one of&rdquo; condition leaves them out. Alchemer&apos;s live count
        adds them as they complete, though a count from scratch leaves them out.
      </Box>
      <Box sx={{ mt: 1 }}>
        A required question can still be blank if display logic hides it, so blanks may also be people who never saw it.
      </Box>
      {counts && 'ours' in counts && (
        <Box sx={{ mt: 1 }}>
          When imported, Alchemer counted <strong>{counts.alchemer ?? '?'}</strong>; Wrangler{' '}
          <strong>{withSkips(counts.ours, counts.withSkips)}</strong>.
        </Box>
      )}
      {counts && 'and' in counts && (
        <Box sx={{ mt: 1 }}>
          When imported, Alchemer counted <strong>{counts.alchemer ?? '?'}</strong>. With AND, Wrangler would count{' '}
          <strong>{withSkips(counts.and, counts.andWithSkips)}</strong>; with OR, <strong>{withSkips(counts.or, counts.orWithSkips)}</strong>.
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" disabled={busy || Boolean(blockedBy)} onClick={onYes} sx={{ textTransform: 'none' }}>
          Yes, count them
        </Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={onNo} sx={{ textTransform: 'none' }}>
          No, keep it as it is
        </Button>
        {blockedBy && <Typography variant="body2">{blockedBy}</Typography>}
      </Box>
    </Alert>
  );
};

const IncreaseHistory = ({ increases }) => {
  if (!increases?.length) return <Typography variant="body2" color="text.secondary">No changes to the limit yet.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow><TableCell>When</TableCell><TableCell>Change</TableCell><TableCell>By</TableCell></TableRow>
      </TableHead>
      <TableBody>
        {increases.map((inc) => (
          <TableRow key={inc.id}>
            <TableCell>{format(new Date(inc.created_at), 'd MMM yyyy, HH:mm')}</TableCell>
            <TableCell>
              {signed(inc.amount)}
              {inc.method === 'percent' && ` (${Number(inc.percent)}%)`}
              {inc.method === 'set' && ' (limit set)'}
            </TableCell>
            <TableCell>{inc.requested_by || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const asProgress = (q) => ({ ...q, responses: q.complete_count, limit: q.current_limit, reserved: q.reserved_count });

/** Wrangler's 400 for a quota, flattened: {name: '...', logic: '...', ...}. Create's errors are by position. */
const errorsByField = (err) => {
  const fields = err?.response?.data?.fields;
  const forQuota = fields?.quotas?.['0'] || fields?.quotas?.[0] || fields || {};
  if (typeof forQuota !== 'object') return {};
  return Object.fromEntries(Object.entries(forQuota).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : JSON.stringify(v)]));
};

/**
 * The slide-out quota editor (design §3). `quota` is the saved quota when editing, or a draft
 * ({name, description, logic, original_limit, is_on}) when creating or duplicating. Increases,
 * limit corrections, the AND / OR choice and Mark as reviewed act at once; everything else waits
 * for Save.
 */
const QuotaEditor = ({
  surveyId, mode, quota, questions, alchemerCount, joinChoice, skipFlag, skipsKept, onKeepSkips, setNames = [], onSaved,
  onDeleted, onReload, onClose, onNext, nextLabel, onDuplicate,
}) => {
  const creating = mode === 'create';
  const index = useMemo(() => indexQuestions(questions), [questions]);
  const [name, setName] = useState(quota.name || '');
  const [description, setDescription] = useState(quota.description || '');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isOn, setIsOn] = useState(Boolean(quota.is_on));
  const [quotaSet, setQuotaSet] = useState(quota.set_name || '');
  const [limit, setLimit] = useState(creating ? String(quota.original_limit ?? '') : '');
  const [tree, setTree] = useState(() => fromLogic(quota.logic, { joinUnknown: quota.group_join_unknown }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [refreshOffer, setRefreshOffer] = useState(null);
  const [dialog, setDialog] = useState(null); // 'increase' | 'set-limit' | 'delete' | 'confirm-on'
  const [skipRecount, setSkipRecount] = useState(null); // {questionIds, before, after} once Yes has saved

  const problems = validate(tree, index);
  const logicChanged = creating || !sameLogic(tree, quota.logic) || tree.op === 'unknown';
  const changes = creating ? null : {
    ...(name.trim() !== quota.name && { name: name.trim() }),
    ...(description !== (quota.description || '') && { description }),
    ...(isOn !== quota.is_on && { is_on: isOn }),
    ...(quotaSet.trim() !== (quota.set_name || '') && { set_name: quotaSet.trim() }),
    ...(logicChanged && problems.length === 0 && { logic: toLogic(tree) }),
  };
  const limitNumber = Number(limit);
  const limitValid = !creating || (limit !== '' && Number.isInteger(limitNumber) && limitNumber >= 0);
  const dirty = creating || Object.keys(changes).length > 0 || (logicChanged && problems.length > 0);
  const canSave = dirty && problems.length === 0 && name.trim() !== '' && limitValid && !busy;
  const countsDiffer = !creating && quota.alchemer_quota_id != null && alchemerCount != null && alchemerCount !== quota.complete_count;
  const change = creating ? 0 : quota.current_limit - quota.original_limit;
  // Asked while the import is being reviewed, until answered: Yes leaves no plain not_in on those questions.
  const skipQuestions = !creating && skipFlag && quota.needs_review && !skipsKept
    ? uncountedSkips(quota.logic, skipFlag.skippable_not_in_questions || []) : [];
  const skipBlockedBy = quota.group_join_unknown ? 'Choose AND or OR first.'
    : !sameLogic(tree, quota.logic) ? 'Save or undo your changes to the conditions first.' : null;

  const act = async (request, after) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await request();
      after(data);
    } catch (err) {
      setError([wranglerError(err), ...fieldErrors(err)].join('\n'));
      setErrors(errorsByField(err));
    } finally {
      setBusy(false);
    }
  };

  const save = (confirmed = false) => {
    // Switching ON a copy of an Alchemer quota: both now block respondents, so say so first.
    if (!creating && changes.is_on === true && quota.alchemer_quota_id != null && !confirmed) {
      setDialog('confirm-on');
      return;
    }
    setDialog(null);
    setErrors({});
    if (creating) {
      const draft = {
        name: name.trim(), description, logic: toLogic(tree), original_limit: limitNumber, is_on: isOn,
        ...(quotaSet.trim() && { set_name: quotaSet.trim() }),
      };
      act(() => api.post(`/surveys/${surveyId}/wrangler/quotas/`, { quotas: [draft] }), (data) => {
        onSaved(data.quotas[0], { created: true });
        if (data.needs_refresh) setRefreshOffer('needed'); else onClose();
      });
    } else {
      act(() => api.patch(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`, changes), (data) => {
        onSaved(data.quota);
        if (data.needs_refresh) setRefreshOffer('needed'); else onClose();
      });
    }
  };

  const queueRefresh = () => act(() => api.post(`/surveys/${surveyId}/wrangler/refresh/`), () => setRefreshOffer('queued'));
  const markReviewed = () => act(() => api.patch(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`, { needs_review: false }),
    (data) => onSaved(data.quota));
  const chooseJoin = (join) => act(() => api.post(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/set-group-join/`, { join }),
    (data) => {
      onSaved(data.quota);
      setTree(fromLogic(data.quota.logic));
    });
  const countSkips = () => {
    const before = quota.complete_count;
    act(() => api.patch(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`, { logic: countingSkips(quota.logic, skipQuestions) }),
      (data) => {
        onSaved(data.quota);
        setTree(fromLogic(data.quota.logic));
        setSkipRecount({ questionIds: skipQuestions, before, after: data.quota.complete_count });
        if (data.needs_refresh) setRefreshOffer('needed');
      });
  };
  const remove = () => act(() => api.delete(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/`), () => {
    onDeleted(quota.id);
    onClose();
  });
  const limitDone = () => {
    setDialog(null);
    onReload();
  };

  const title = creating ? (name.trim() || 'New quota') : safeDecode(quota.name);
  const chips = creating ? <StatusChip label="New" colour="grey" /> : (
    <>
      {quota.is_on ? <StatusChip label="ON" colour="success" /> : <StatusChip label="OFF" colour="grey" />}
      {quota.closed && <StatusChip label="Closed" colour="error" />}
      {quota.needs_review && <StatusChip label="Needs review" colour="warning" />}
      {quota.group_join_unknown && <StatusChip label="AND/OR needed" colour="warning" />}
    </>
  );
  const historyCount = quota.increases?.length || 0;

  const footer = (
    <>
      {refreshOffer === 'needed' && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={queueRefresh} disabled={busy} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Run a refresh</Button>}>
          Saved. Some completes so far don&apos;t have answers to the questions this quota uses, so its count is too low.
          Run a refresh to count them.
        </Alert>
      )}
      {refreshOffer === 'queued' && <Alert severity="success" sx={{ mb: 1.5 }}>Refresh queued. Follow it on the Setup tab.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5, whiteSpace: 'pre-line', maxHeight: 160, overflowY: 'auto' }}>{error}</Alert>}
      <FooterActions
        secondary={!creating && (
          <>
            {onDuplicate && (
              <Button onClick={() => onDuplicate(quota)} disabled={busy} sx={{ textTransform: 'none' }}>Duplicate</Button>
            )}
            <Button color="error" onClick={() => setDialog('delete')} disabled={busy} sx={{ textTransform: 'none' }}>Delete</Button>
          </>
        )}
        primary={refreshOffer ? (
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        ) : (
          <>
            {onNext && !creating && !dirty && (
              <Button onClick={onNext} disabled={busy} sx={{ textTransform: 'none' }}>{nextLabel}</Button>
            )}
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={() => save()} disabled={!canSave} sx={{ textTransform: 'none' }}>
              {busy ? 'Saving…' : creating ? 'Create' : 'Save'}
            </Button>
          </>
        )}
      />
    </>
  );

  return (
    <Panel busy={busy} onClose={onClose} footer={footer}>
      <PanelHeader title={title} chips={chips} onClose={onClose} busy={busy} />

      <PanelAlerts>
        {!creating && quota.group_join_unknown && (
          <Alert severity="warning">
            Alchemer didn&apos;t reveal whether this quota&apos;s groups combine with AND or OR. Choose one; the quota
            can&apos;t be switched ON until you do.
            {joinChoice && (
              <Box sx={{ mt: 1 }}>
                Alchemer counts <strong>{joinChoice.alchemer ?? '?'}</strong>. With AND it would count{' '}
                <strong>{withSkips(joinChoice.and, joinChoice.and_with_skips)}</strong>; with OR,{' '}
                <strong>{withSkips(joinChoice.or, joinChoice.or_with_skips)}</strong>.
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => chooseJoin('and')} sx={{ textTransform: 'none' }}>Use AND</Button>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => chooseJoin('or')} sx={{ textTransform: 'none' }}>Use OR</Button>
            </Box>
          </Alert>
        )}
        {skipQuestions.length > 0 && (
          <SkipQuestion
            questionIds={skipQuestions}
            index={index}
            countCheck={skipFlag.count_check}
            showCounts={!quota.group_join_unknown && skipFlag.count_check?.matches_alchemer !== true}
            blockedBy={skipBlockedBy}
            busy={busy}
            onYes={countSkips}
            onNo={onKeepSkips}
          />
        )}
        {skipRecount && (
          <Alert severity="success" onClose={() => setSkipRecount(null)}>
            Now counts people who skip {andList(skipRecount.questionIds.map((id) => quoted(index, id)))}. Recounted:{' '}
            <strong>{skipRecount.before} → {skipRecount.after}</strong>
            {alchemerCount != null && <> (Alchemer {alchemerCount})</>}.
          </Alert>
        )}
        {!creating && quota.needs_review && (
          <Alert severity="info" action={(
            <Button color="inherit" size="small" disabled={busy || quota.group_join_unknown} onClick={markReviewed}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Mark as reviewed
            </Button>
          )}>
            Imported from Alchemer. Check that the summary under &quot;Who it counts&quot; says what Alchemer&apos;s quota means.
          </Alert>
        )}
        {countsDiffer && (
          <Alert severity="warning">
            <strong>Counts differ: Wrangler {quota.complete_count}, Alchemer {alchemerCount}.</strong>{' '}
            Alchemer&apos;s own counts can lag behind its responses, so a small difference may be Alchemer&apos;s.
            Check the logic first{quota.needs_review ? ', before marking as reviewed.' : '.'}
            {skipQuestions.length > 0 && ' People who skip may explain some of it: see the question above.'}
          </Alert>
        )}
      </PanelAlerts>

      <PanelSection title="Details">
        <TextField label="Name" size="small" fullWidth required value={name} onChange={(e) => setName(e.target.value)}
          error={Boolean(errors.name)} helperText={errors.name} sx={{ mb: 2 }} />
        <Autocomplete
          freeSolo
          options={setNames}
          inputValue={quotaSet}
          onInputChange={(e, value) => setQuotaSet(value)}
          renderInput={(params) => (
            <TextField {...params} label="Set (optional)" size="small" error={Boolean(errors.set_name)}
              helperText={errors.set_name || 'Quotas with the same set name form a set, for rules and the tracker.'} />
          )}
          sx={{ mb: 2 }}
        />
        <TextField label="Description (optional)" size="small" fullWidth value={description}
          onChange={(e) => setDescription(e.target.value)} error={Boolean(errors.description)} helperText={errors.description} />
      </PanelSection>

      <PanelSection title="Who it counts">
        <SummaryBox>
          {problems.length === 0
            ? <LogicSummary logic={toLogic(tree)} questions={questions} variant="body1" />
            : <Typography variant="body1" color="text.secondary">Finish the conditions below to see what this quota counts.</Typography>}
        </SummaryBox>
        <BuilderArea>
          <ConditionBuilder tree={tree} onChange={setTree} questions={questions} index={index} problems={problems} />
        </BuilderArea>
        {errors.logic && <Alert severity="error" sx={{ mt: 1.5 }}>{errors.logic}</Alert>}
        {!creating && logicChanged && quota.complete_count > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Saving recalculates this quota&apos;s count from the completes so far. It holds the survey while it runs,
            so it&apos;s best done away from peak fieldwork.
          </Alert>
        )}
      </PanelSection>

      <PanelSection title="Limit and progress">
        {creating ? (
          <TextField label="Limit" size="small" type="number" required value={limit} onChange={(e) => setLimit(e.target.value)}
            error={Boolean(errors.original_limit) || (limit !== '' && !limitValid)} helperText={errors.original_limit}
            sx={{ width: 160 }} />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" component="p" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {quota.complete_count} / {quota.current_limit}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                completes / limit{change !== 0 && ` (original ${quota.original_limit}, ${signed(change)})`}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {quota.reserved_count} holding a place
              {quota.alchemer_quota_id != null && alchemerCount != null && !countsDiffer && (
                <> · <span>Its Alchemer original counts {alchemerCount} too.</span></>
              )}
            </Typography>
            <Box sx={{ mb: 2 }}><ProgressBar quota={asProgress(quota)} /></Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={() => setDialog('increase')} disabled={busy} sx={{ textTransform: 'none' }}>Increase…</Button>
              <Button size="small" variant="outlined" onClick={() => setDialog('set-limit')} disabled={busy} sx={{ textTransform: 'none' }}>Set limit…</Button>
            </Box>
            <Button size="small" onClick={() => setHistoryOpen((o) => !o)} aria-expanded={historyOpen}
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: historyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
              sx={{ textTransform: 'none', px: 0.5 }}>
              Limit history ({historyCount})
            </Button>
            <Collapse in={historyOpen}><Box sx={{ mt: 1 }}><IncreaseHistory increases={quota.increases} /></Box></Collapse>
          </>
        )}
      </PanelSection>

      <PanelSection title="Status">
        <FormControlLabel
          control={<Switch checked={isOn} disabled={!creating && quota.group_join_unknown && !quota.is_on} onChange={(e) => setIsOn(e.target.checked)} />}
          label={isOn ? 'ON: blocks respondents when full' : 'OFF: counts only'}
        />
        {!creating && quota.group_join_unknown && !quota.is_on && (
          <Typography variant="body2" color="text.secondary">Choose AND or OR (above) before switching it ON.</Typography>
        )}
      </PanelSection>

      {dialog === 'increase' && <IncreaseDialog surveyId={surveyId} quota={quota} onClose={() => setDialog(null)} onDone={limitDone} />}
      {dialog === 'set-limit' && <SetLimitDialog surveyId={surveyId} quota={quota} onClose={() => setDialog(null)} onDone={limitDone} />}
      <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)}>
        <DialogTitle>Delete {safeDecode(quota.name)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Its limit history goes with it, and quota views and trackers that include it lose it. Respondents it would
            have blocked are let through. This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={dialog === 'confirm-on'} onClose={() => setDialog(null)}>
        <DialogTitle>Switch ON {safeDecode(quota.name)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is a copy of an Alchemer quota, and the Alchemer original is still active, so both will now block
            respondents. The copy may close slightly earlier, because it also counts people holding a place.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => save(true)} sx={{ textTransform: 'none' }}>Switch ON and save</Button>
        </DialogActions>
      </Dialog>
    </Panel>
  );
};

export default QuotaEditor;
